import type { Designer } from "@nlc/shared-types";

import { Info, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useListData } from "react-stately";

import { Button } from "@/components/button";
import { MultiSelect2 } from "@/components/multi-select-2/MultiSelect2";
import { FeaturePageLayout } from "@/features/shared/layout";

import { usePreferredDesigners } from "./hooks/usePreferredDesigners";
import { useUpdatePreferences } from "./hooks/useUpdatePreferences";

interface DesignerWithTextValue extends Designer {
  textValue: string;
}

export default function PreferredDesignersPage() {
  const [hiddenBanner, setHiddenBanner] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const { data, isLoading } = usePreferredDesigners();
  const updateMutation = useUpdatePreferences();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedUpdate = useCallback(
    (preferredIds: number[], nonPreferredIds: number[]) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        updateMutation.mutate({
          nonPreferredDesignerIds: nonPreferredIds,
          preferredDesignerIds: preferredIds,
        });
      }, 500);
    },
    [updateMutation],
  );

  const preferredDesigners = useListData<DesignerWithTextValue>({
    getKey: (item) => item.id,
    initialItems: [],
  });

  const nonPreferredDesigners = useListData<DesignerWithTextValue>({
    getKey: (item) => item.id,
    initialItems: [],
  });

  useEffect(() => {
    if (data && !isInitialized) {
      preferredDesigners.setSelectedKeys("all");
      preferredDesigners.removeSelectedItems();
      for (const designer of data.preferredDesigners) {
        preferredDesigners.append({
          ...designer,
          textValue: designer.name,
        });
      }

      nonPreferredDesigners.setSelectedKeys("all");
      nonPreferredDesigners.removeSelectedItems();
      for (const designer of data.nonPreferredDesigners) {
        nonPreferredDesigners.append({
          ...designer,
          textValue: designer.name,
        });
      }

      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isInitialized]);

  const designerItems = useMemo(
    () =>
      (data?.availableDesigners || []).map((d) => ({
        avatar: d.profilePicture || undefined,
        href: `/users/${d.id}/edit`,
        id: d.id,
        textValue: d.name,
      })),
    [data?.availableDesigners],
  );

  const handleDismiss = () => {
    setHiddenBanner(true);
  };

  const handleRemovePreferred = (key: React.Key) => {
    if (!isInitialized) return;

    const newPreferredIds = preferredDesigners.items
      .filter((d) => d.id !== key)
      .map((d) => d.id);
    const newNonPreferredIds = nonPreferredDesigners.items.map((d) => d.id);

    preferredDesigners.remove(key as number);

    updateMutation.mutate({
      nonPreferredDesignerIds: newNonPreferredIds,
      preferredDesignerIds: newPreferredIds,
    });
  };

  const handleAddPreferred = (id: number) => {
    if (!isInitialized) return;

    const designer = data?.availableDesigners.find((d) => d.id === id);

    if (!designer) return;

    const newNonPreferredIds = nonPreferredDesigners.items
      .filter((d) => d.id !== id)
      .map((d) => d.id);
    const newPreferredIds = preferredDesigners.getItem(id)
      ? preferredDesigners.items.map((d) => d.id)
      : [...preferredDesigners.items.map((d) => d.id), id];

    if (nonPreferredDesigners.getItem(id)) {
      nonPreferredDesigners.remove(id);
    }

    if (!preferredDesigners.getItem(id)) {
      preferredDesigners.append({
        ...designer,
        textValue: designer.name,
      });
    }

    debouncedUpdate(newPreferredIds, newNonPreferredIds);
  };

  const handleRemoveNonPreferred = (key: React.Key) => {
    if (!isInitialized) return;

    const newNonPreferredIds = nonPreferredDesigners.items
      .filter((d) => d.id !== key)
      .map((d) => d.id);
    const newPreferredIds = preferredDesigners.items.map((d) => d.id);

    nonPreferredDesigners.remove(key as number);

    updateMutation.mutate({
      nonPreferredDesignerIds: newNonPreferredIds,
      preferredDesignerIds: newPreferredIds,
    });
  };

  const handleAddNonPreferred = (id: number) => {
    if (!isInitialized) return;

    const designer = data?.availableDesigners.find((d) => d.id === id);

    if (!designer) return;

    const newPreferredIds = preferredDesigners.items
      .filter((d) => d.id !== id)
      .map((d) => d.id);
    const newNonPreferredIds = nonPreferredDesigners.getItem(id)
      ? nonPreferredDesigners.items.map((d) => d.id)
      : [...nonPreferredDesigners.items.map((d) => d.id), id];

    if (preferredDesigners.getItem(id)) {
      preferredDesigners.remove(id);
    }

    if (!nonPreferredDesigners.getItem(id)) {
      nonPreferredDesigners.append({
        ...designer,
        textValue: designer.name,
      });
    }

    updateMutation.mutate({
      nonPreferredDesignerIds: newNonPreferredIds,
      preferredDesignerIds: newPreferredIds,
    });
  };

  return (
    <div className="p-xl">
      <FeaturePageLayout title="My Designers">
        <div className="bg-bg-primary p-5 position-relative">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-text-tertiary">Loading designers...</p>
            </div>
          )}

          {!isLoading && !hiddenBanner && (
            <div className="relative mb-6 rounded-xl bg-bg-primary p-6 border border-[#D0D5DD]">
              <Button
                className="absolute top-3 right-3 p-1.5 text-text-tertiary hover:text-text-primary"
                onPress={handleDismiss}
                variant="plain"
              >
                <X className="h-5 w-5" />
              </Button>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EBE9FE]">
                    <Info className="h-5 w-5 text-[#6938EF]" />
                  </div>
                </div>
                <div className="flex-1 pr-8">
                  <h3 className="mb-1 text-sm font-semibold text-text-primary">
                    We&apos;ve just released a new feature
                  </h3>
                  <p className="text-sm text-text-secondary">
                    Hover over designer chips to see details, drag & drop to
                    move between sections.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && (
            <>
              <div className="mb-8">
                <MultiSelect2
                  label="Preferred designers"
                  onRemove={(key) => handleRemovePreferred(key as number)}
                  onSelect={(key) => handleAddPreferred(key as number)}
                  options={designerItems}
                  placeholder="Search to add preferred designers..."
                  selectedIds={preferredDesigners.items.map((d) => d.id)}
                />
              </div>

              <div>
                <MultiSelect2
                  label="Non-preferred designers"
                  onRemove={(key) => handleRemoveNonPreferred(key as number)}
                  onSelect={(key) => handleAddNonPreferred(key as number)}
                  options={designerItems}
                  placeholder="Search to add non-preferred designers..."
                  selectedIds={nonPreferredDesigners.items.map((d) => d.id)}
                />
              </div>
            </>
          )}
        </div>
      </FeaturePageLayout>
    </div>
  );
}
